import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  Tooltip,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Divider,
} from "@mui/material";
import { CacheKey } from "../../../types/cache-keys";
import { validId } from "../../../hooks/base-hooks";
import ResourceRelationPanel from "../Panels/ResourceRelationPanel";
import TagManagementSection from "../Common/TagManagementSection";
import {
  useAddGtmSolutionComponentMutation,
  useAttachedItemsUpdateMutation,
  useFetchSingleGtmArchitecture,
  useFetchSingleGtmSolutionComponent,
  useUpdateGtmSolutionComponentMutation,
} from "../../../hooks/gtm-solution-hooks";
import { useFetchCiscoProducts, useFetchProductFeatures } from "../../../hooks/product-hooks";
import { Product } from "../../../types/product";
import { GtmProductFeature, GtmSolutionComponent } from "../../../types/go-to-market";
import SnackableCourseButton from "../SnackableCourse/SnackableCourseButton";
import SnackableCourseManager from "../SnackableCourse/SnackableCourseManager";
import { asArray } from "../../../support/utils";
import AdminConfirmDialog from "../Common/AdminConfirmDialog";

type Props = {
  open: boolean;
  mode: "create" | "edit";
  gtmId: number;
  componentId?: number;
  onClose: () => void;
  onSaved: () => void;
  readOnly?: boolean;
};

export default function GtmSolutionComponentEditorDialog({
                                                           open,
                                                           mode,
                                                           gtmId,
                                                           componentId,
                                                           onClose,
                                                           onSaved,
                                                           readOnly = false,
                                                         }: Props) {
  const isEdit = mode === "edit" && validId(componentId);
  const [hasHydrated, setHasHydrated] = useState(false);
  const [courseManagerOpen, setCourseManagerOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const { data: ciscoProducts, isLoading: productsLoading } = useFetchCiscoProducts();
  const ciscoProductById = useMemo(() => {
    if (!ciscoProducts) return new Map<number, Product>();
    const m = new Map<Product["id"], Product>();
    for (const p of ciscoProducts) m.set(p.id, p);
    return m;
  }, [ciscoProducts]);

  const { data: architecture, isLoading: architectureLoading } = useFetchSingleGtmArchitecture(gtmId);
  const { data: componentData, isLoading: componentLoading } = useFetchSingleGtmSolutionComponent(gtmId, componentId);

  const component = useMemo(() => {
    if (isEdit) {
      if (architectureLoading && componentLoading) return null;
      if (componentData) return componentData;
      const found = asArray(architecture?.component).find((s) => s.id === componentId);
      return found ?? null;
    }
    return null;
  }, [isEdit, componentId, architecture, architectureLoading, componentData, componentLoading]);

  const { mutateAsync: addComponent, isPending: addingComponent } = useAddGtmSolutionComponentMutation(gtmId);
  const { mutateAsync: updateComponent, isPending: updatingComponent } = useUpdateGtmSolutionComponentMutation(gtmId);
  const { mutateAsync: updateJoins, isPending: updatingJoins } = useAttachedItemsUpdateMutation<
    GtmProductFeature,
    GtmSolutionComponent
  >({
    type: CacheKey.GtmSolutionComponentFeature,
    architectureId: gtmId,
  });

  const [form, setForm] = useState<any>({
    name: "",
    short_description: "",
    description: "",
    go_to_market: gtmId || null,
    product: null,
  });
  const [initialForm, setInitialForm] = useState<any>({
    name: "",
    short_description: "",
    description: "",
    go_to_market: gtmId || null,
    product: null,
  });

  const [selectedProducts, setSelectedProducts] = useState<Record<Product["id"], GtmProductFeature["id"][]>>({});
  const [initialSelectedProducts, setInitialSelectedProducts] = useState<Record<Product["id"], GtmProductFeature["id"][]>>({});

  const selectedProductChips = useMemo(() => {
    return Object.entries(selectedProducts)
      .filter(([k, arr]) => (arr || []).length > 0 && ciscoProductById.has(Number(k)))
      .map(([k]) => ciscoProductById.get(Number(k)));
  }, [selectedProducts, ciscoProductById]);

  const activeProductId: Product["id"] | null = form.product ? Number(form.product) : null;

  const { data: features, isLoading: featuresLoading } = useFetchProductFeatures(activeProductId);

  const featuresById = useMemo(() => {
    const map = new Map<GtmProductFeature["id"], GtmProductFeature>();
    for (const feature of features ?? []) map.set(feature.id, feature);
    return map;
  }, [features]);

  const activeSelectedFeatures = useMemo(() => {
    if (!activeProductId) return [];
    return (selectedProducts[activeProductId] ?? []).map((id) => featuresById.get(id)).filter(Boolean);
  }, [activeProductId, selectedProducts, featuresById]);

  useEffect(() => {
    if (!open) {
      setHasHydrated(false);
      return;
    }
  }, [open]);

  useEffect(() => {
    setHasHydrated(false);
  }, [mode, componentId, gtmId]);

  useEffect(() => {
    if (!open || hasHydrated || mode !== "create") return;

    const emptyForm = {
      name: "",
      short_description: "",
      description: "",
      go_to_market: gtmId ?? null,
      product: null,
    };

    setForm(emptyForm);
    setInitialForm(emptyForm);
    setSelectedProducts({});
    setInitialSelectedProducts({});
    setHasHydrated(true);
  }, [open, hasHydrated, mode, gtmId]);

  useEffect(() => {
    if (!open || hasHydrated || !isEdit || !component) return;

    const nextForm = {
      ...component,
      name: component.name ?? "",
      short_description: component.short_description ?? "",
      description: component.description ?? "",
      go_to_market: gtmId || component?.go_to_market || null,
      product: component.product ?? null,
    };

    const featureByProduct: Record<Product["id"], Set<GtmProductFeature["id"]>> = {};

    for (const feature of component?.product_feature ?? []) {
      const productId = typeof feature?.product === "number" ? feature.product : feature?.product?.id ?? null;
      if (!productId) continue;

      if (!featureByProduct[productId]) featureByProduct[productId] = new Set();
      featureByProduct[productId].add(feature.id);
    }

    const products: Record<Product["id"], GtmProductFeature["id"][]> = Object.fromEntries(
      Object.entries(featureByProduct).map(([productId, featureIds]) => [Number(productId), Array.from(featureIds)])
    );

    setForm(nextForm);
    setInitialForm(nextForm);
    setSelectedProducts(products);
    setInitialSelectedProducts(products);

    if (!component.product && Object.keys(products).length > 0) {
      setForm((prev: any) => ({ ...prev, product: Number(Object.keys(products)[0]) }));
    }

    setHasHydrated(true);
  }, [open, hasHydrated, isEdit, component, gtmId]);

  const canManageRelations = isEdit;

  const saving = addingComponent || updatingComponent || updatingJoins || componentLoading;

  const normalizedSelectedProducts = useMemo(() => {
    return Object.fromEntries(
      Object.entries(selectedProducts)
        .map(([pid, ids]) => [pid, [...ids].sort((a, b) => a - b)])
        .sort(([a], [b]) => Number(a) - Number(b))
    );
  }, [selectedProducts]);

  const normalizedInitialSelectedProducts = useMemo(() => {
    return Object.fromEntries(
      Object.entries(initialSelectedProducts)
        .map(([pid, ids]) => [pid, [...ids].sort((a, b) => a - b)])
        .sort(([a], [b]) => Number(a) - Number(b))
    );
  }, [initialSelectedProducts]);

  const hasUnsavedChanges = useMemo(() => {
    return (
      form.name !== initialForm.name ||
      form.short_description !== initialForm.short_description ||
      form.description !== initialForm.description ||
      form.product !== initialForm.product ||
      JSON.stringify(normalizedSelectedProducts) !== JSON.stringify(normalizedInitialSelectedProducts)
    );
  }, [normalizedSelectedProducts, normalizedInitialSelectedProducts, form.name, initialForm.name, form.short_description, initialForm.short_description, form.description, initialForm.description, form.product, initialForm.product]);

  const resetAndClose = () => {
    setHasHydrated(false);
    setForm({
      name: "",
      short_description: "",
      description: "",
      go_to_market: gtmId || null,
      product: null,
    });
    setInitialForm({
      name: "",
      short_description: "",
      description: "",
      go_to_market: gtmId || null,
      product: null,
    });
    setSelectedProducts({});
    setInitialSelectedProducts({});
    setConfirmCloseOpen(false);
    onClose();
  };

  const handleCloseDialog = () => {
    if (readOnly || !hasUnsavedChanges) {
      resetAndClose();
      return;
    }
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    resetAndClose();
  };

  const handleSave = async () => {
    if (readOnly) {
      handleCloseDialog();
      return;
    }

    let finalId = null;

    if (!isEdit) {
      const created: GtmSolutionComponent = await addComponent({ ...form, go_to_market: gtmId });
      finalId = created?.id;
    } else if (isEdit && componentId) {
      await updateComponent({ ...form, go_to_market: gtmId, id: componentId });
      finalId = componentId;
    }

    if (finalId) {
      const selectedFeatures = new Set(Object.values(selectedProducts).flat());
      await updateJoins({ selectedItems: selectedFeatures, attachingToId: finalId });
    }

    onSaved();
    resetAndClose();
  };

  const handleSelectedFeaturesChange = (newIds: number[]) => {
    if (!activeProductId) return;

    setSelectedProducts((prev) => {
      const next = { ...prev, [activeProductId]: newIds };
      if (!newIds.length) delete next[activeProductId];
      return next;
    });
  };

  const handleProductChipClick = (pid: number | null) => {
    if (readOnly) return;
    setForm((prev: any) => ({ ...prev, product: pid }));
  };

  const handleProductChipDelete = (pid: number) => {
    if (!readOnly) {
      setSelectedProducts((prev) => {
        const { [pid]: _, ...next } = prev;
        return next;
      });
      if (activeProductId === pid) {
        setForm((prev: any) => ({ ...prev, product: null }));
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {readOnly
                ? mode === "create"
                  ? "View Solution Component"
                  : `View Component: ${form?.name || `#${componentId}`}`
                : mode === "create"
                  ? "Create Solution Component"
                  : `Component: ${form?.name || `#${componentId}`}`}
            </Typography>
            {isEdit && (
              <SnackableCourseButton
                entityType="solution_component"
                entityId={componentId}
                entityName={form.name}
                disabled={!isEdit}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          {saving && (
            <Box display="flex" alignItems="center" gap={2} py={1}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading…</Typography>
            </Box>
          )}

          <TextField
            fullWidth
            margin="normal"
            label="Name"
            value={form.name ?? ""}
            onChange={(e) => {
              if (readOnly) return;
              setForm((p: any) => ({ ...p, name: e.target.value }));
            }}
            disabled={readOnly}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Short Description"
            value={form.short_description ?? ""}
            onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, short_description: e.target.value }))}
            multiline
            minRows={2}
            disabled={readOnly}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Description"
            value={form.description ?? ""}
            onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, description: e.target.value }))}
            multiline
            minRows={2}
            disabled={readOnly}
          />

          <Box mt={2}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Products with selected features
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {selectedProductChips.length ? (
                selectedProductChips.map((p: any) => {
                  const pid = p.id;
                  const isSelected = activeProductId === pid;
                  const count = (selectedProducts[pid] || []).length;

                  return (
                    <Chip
                      key={pid}
                      label={`${p.name} (${count})`}
                      size="small"
                      color={isSelected ? "primary" : "default"}
                      variant={isSelected ? "filled" : "outlined"}
                      onClick={() => handleProductChipClick(pid)}
                      onDelete={readOnly ? undefined : () => handleProductChipDelete(pid)}
                    />
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No products selected yet.
                </Typography>
              )}
            </Box>
          </Box>

          <Box mt={2}>
            <FormControl fullWidth disabled={productsLoading || readOnly}>
              <InputLabel id="sc-product-label">Cisco Product</InputLabel>
              <Select
                labelId="sc-product-label"
                label="Cisco Product"
                value={form.product || ""}
                onChange={(e) => {
                  if (readOnly) return;
                  const val = e.target.value && e.target.value !== "" ? Number(e.target.value) : null;
                  setForm((p: any) => ({ ...p, product: val }));
                }}
              >
                <MenuItem value="">
                  <em>{productsLoading ? "Loading products..." : "Select a Cisco product"}</em>
                </MenuItem>
                {(ciscoProducts ?? []).map((p: any) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box mt={2}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Supporting Features {activeProductId ? `for ${ciscoProductById.get(activeProductId)?.name}` : "(select a product first)"}
            </Typography>

            <Autocomplete
              multiple
              disableCloseOnSelect
              disabled={!activeProductId || readOnly}
              options={features ?? []}
              value={activeSelectedFeatures}
              isOptionEqualToValue={(opt: any, val: any) => opt.id === val.id}
              getOptionLabel={(opt: any) => opt?.name ?? ""}
              onChange={(_, newValues) => {
                if (readOnly) return;
                const ids = newValues.map((v: any) => v.id);
                handleSelectedFeaturesChange(ids);
              }}
              renderOption={(props, option: any, { selected }) => (
                <li {...props} key={option.id}>
                  <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                  <Tooltip title={option.description || ""} placement="right" arrow>
                    <Box sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>{option.name}</Box>
                  </Tooltip>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={featuresLoading ? "Loading features..." : "Select Supporting Features"}
                  placeholder={
                    activeProductId
                      ? featuresLoading
                        ? "Loading features..."
                        : "Search features..."
                      : "Select a product first"
                  }
                />
              )}
              loading={featuresLoading && !!activeProductId}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {canManageRelations && (
            <Box mb={3}>
              <ResourceRelationPanel
                joinEndpoint="db/solution-component-to-resource"
                joinCacheKey={CacheKey.GtmSolutionComponentToResource}
                parentField="solution_component"
                parentId={componentId ?? null}
                title="Resources"
                showNameColumn={false}
                maxHeight={300}
                invalidateKeys={[CacheKey.GtmSolutionComponent]}
                disabled={!canManageRelations}
                readOnly={readOnly}
              />
            </Box>
          )}

          <TagManagementSection
            parentId={componentId ?? null}
            parentType={CacheKey.GtmSolutionComponent}
            disabled={!canManageRelations || readOnly}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button onClick={handleSave} variant="contained" disabled={saving}>
              Save
            </Button>
          )}
        </DialogActions>

        {courseManagerOpen && (
          <SnackableCourseManager
            open={courseManagerOpen}
            entityType="solution_component"
            entityId={componentId}
            onClose={() => setCourseManagerOpen(false)}
          />
        )}
      </Dialog>

      <AdminConfirmDialog
        open={confirmCloseOpen}
        title="Discard Changes"
        message={
          <Box>
            <Typography variant="body1" gutterBottom>
              You have unsaved changes in this solution component editor.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              If you continue, your changes will be lost.
            </Typography>
          </Box>
        }
        confirmLabel="Discard Changes"
        cancelLabel="Continue Editing"
        confirmColor="warning"
        loading={saving}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
}